import { useQueryClient } from '@tanstack/react-query'
import { Switch } from '@/common/ui/switch'
import { Badge } from '@/common/ui/badge'
import { Loader2, Wallet, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdatePaymentGateway from '@/modules/bank/api/hooks/useMutationUpdatePaymentGateway'
import { BankQueryKeys } from '@/modules/bank/keys'
import LoadingOverlay from '@/common/components/LoadingOverlay'
import { cn } from '@/common/lib/utils'

import type {
	GetPaymentGateWayResponseData,
	PaymentChannelTab,
} from '@/modules/bank/type'

interface TogglePaymentGatewayWithdrawalAccountProps {
	row: { original: GetPaymentGateWayResponseData }
	channel?: PaymentChannelTab
}

export default function TogglePaymentGatewayWithdrawalAccount({
	row,
	channel,
}: TogglePaymentGatewayWithdrawalAccountProps) {
	const { mutateAsync: updatePaymentGateway, isPending } =
		useMutationUpdatePaymentGateway()
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const isWithdrawalAccount = row.original.isWithdrawalAccount || false

	const handleChange = async (checked: boolean) => {
		setIsLoading(true)
		try {
			await updatePaymentGateway({
				payload: {
					id: row.original.id,
					isWithdrawalAccount: checked,
					...(channel ? { channel: channel as string } : {}),
				},
			})
			queryClient.invalidateQueries({
				queryKey: [BankQueryKeys.PaymentGateways],
			})
			queryClient.invalidateQueries({
				queryKey: [BankQueryKeys.ChannelCounts],
			})
			toast.success('อัพเดทบัญชีถอนเงินสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<>
			<LoadingOverlay
				open={isPending || isLoading}
				message="กำลังอัพเดทบัญชีถอนเงิน ธนาคารออโต้..."
			/>
			<div className="flex items-center gap-3">
				<Switch
					checked={isWithdrawalAccount}
					onCheckedChange={handleChange}
					disabled={isLoading || isPending}
					className="data-[state=checked]:bg-blue-600"
				/>
				{isLoading || isPending ? (
					<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
				) : (
					<Badge
						variant={isWithdrawalAccount ? 'default' : 'secondary'}
						className={cn(
							'flex items-center gap-1.5 px-2 py-0.5',
							isWithdrawalAccount
								? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
								: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
						)}
					>
						{isWithdrawalAccount ? (
							<Wallet className="h-3.5 w-3.5" />
						) : (
							<X className="h-3.5 w-3.5" />
						)}
					</Badge>
				)}
			</div>
		</>
	)
}
